import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import {
  buildRoomAttendance,
  type RoomAttendance,
  type UserLookup,
} from "../utils/roomAttendance";

type RoomFields = {
  name?: string;
  createdBy?: string;
  startAt?: { toDate?: () => Date };
  participants?: unknown;
};

const toDate = (value: RoomFields["startAt"]): Date | null =>
  typeof value?.toDate === "function" ? value.toDate() : null;

/**
 * Every user, keyed by uid, so attendance rows can show names and emails
 * without a lookup per participant.
 */
async function loadUsers(): Promise<UserLookup> {
  const snapshot = await getDocs(collection(db, "users"));
  const users: UserLookup = {};
  snapshot.docs.forEach((userDoc) => {
    const data = userDoc.data() as { name?: string; email?: string };
    users[userDoc.id] = { name: data.name, email: data.email };
  });
  return users;
}

/**
 * The uids that actually turned up to a room.
 *
 * Read from presenceEvents rather than participantsActions: presenceEvents goes
 * back to the first room (2026-05-08), while participantsActions only starts on
 * 2026-07-08 and would report every older room as a total no-show.
 *
 * Queried per room rather than as a collection group — a collection group read
 * needs a `match /{path=**}/presenceEvents/{id}` rule, which the rules do not
 * grant.
 */
async function loadJoinedUids(roomId: string): Promise<string[]> {
  const snapshot = await getDocs(
    query(
      collection(db, "rooms", roomId, "presenceEvents"),
      where("type", "==", "joined"),
    ),
  );
  return snapshot.docs
    .map((eventDoc) => eventDoc.get("uid"))
    .filter((uid): uid is string => typeof uid === "string");
}

/**
 * Attendance for every conversation, newest first: who signed up, who turned
 * up, and who did not. Answers "who did not join" (#64) and "who signed up for
 * each conversation" (#56).
 */
export async function getConversationAttendance(): Promise<RoomAttendance[]> {
  const [usersById, roomsSnapshot] = await Promise.all([
    loadUsers(),
    getDocs(collection(db, "rooms")),
  ]);

  const attendance = await Promise.all(
    roomsSnapshot.docs.map(async (roomDoc) => {
      const data = roomDoc.data() as RoomFields;
      return buildRoomAttendance({
        room: {
          id: roomDoc.id,
          name: data.name ?? "",
          createdBy: data.createdBy ?? "",
          startAt: toDate(data.startAt),
          participants: Array.isArray(data.participants)
            ? data.participants.filter(
                (uid): uid is string => typeof uid === "string",
              )
            : [],
        },
        joinedUids: await loadJoinedUids(roomDoc.id),
        usersById,
      });
    }),
  );

  return attendance.sort((a, b) => {
    const aTime = a.startAt?.getTime() ?? 0;
    const bTime = b.startAt?.getTime() ?? 0;
    return bTime - aTime;
  });
}
